import { Calendar, UserCheck, X } from "lucide-react";
import { useIsMobile } from "../hooks/use-mobile";
import { useLanguage } from "../i18n/LanguageContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Icon } from "@iconify/react";

import { useState } from "react";
import { getSheetMethodContentMap } from "../components/booking/bookingstep/StepChooseMethod/sheetMethodContentMap";
import clsx from "clsx";
import { useDoctors } from "../hooks/useDoctors";
import { ClinicScheduleStatusDto, DoctorDto } from "../web-api-client";
import { useSessionStorageState } from "../hooks/useSessionStorageState";

import { SelectMethodDialog } from "../components/booking/SelectMethodDialog";
import { useClinics } from "../hooks/useClinics";

type BottomSheetType = "specialty" | "doctor";

export const ChooseMethod = () => {
  const [isMobile] = useIsMobile();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedDoctor, setSelectedDoctor] = useSessionStorageState(
    "selectedDoctor",
    null,
  );
  const [selectedDate, setSelectedDate] = useSessionStorageState(
    "selectedDate",
    null,
  );
  const [selectedMonth, setSelectedMonth] = useSessionStorageState(
    "selectedMonth",
    null,
  );
  const [selectedYear, setSelectedYear] = useSessionStorageState(
    "selectedYear",
    null,
  );
  const [selectedTime, setSelectedTime] = useSessionStorageState(
    "selectedTime",
    null,
  );

  const sheetTitleMap: Record<BottomSheetType, string> = {
    specialty: t("chonchuyenkhoa"),
    doctor: t("chonbacsi"),
  };

  const [bottomSheetType, setShowBottomSheetType] = useState<BottomSheetType>();
  const [showBottomSheet, setShowBottomSheet] = useState(false);

  const { doctors, isFetching, isError, avatars, avatarErrors } = useDoctors({
    date: "",
    specialty: null,
    availableOnly: true,
    searchTerm: "",
    onlyConfigRoom: true,
  });

  const { data: specialties, isLoading } = useClinics(true);

  const onOptionSelected = (type: string) => {
    if (type === "byTime") {
      setShowBottomSheetType("specialty");
    } else {
      setShowBottomSheetType("doctor");
    }

    setShowBottomSheet(true);
  };

  const handleSpecialtySelect = (sp: ClinicScheduleStatusDto) => {
    setShowBottomSheet(false);
    resetData();

    setTimeout(() => {
      navigateSpecialtyStep(sp);
    }, 100);
  };

  const handleDoctorSelect = (doctor: DoctorDto) => {
    setShowBottomSheet(false);
    resetData();

    setTimeout(() => {
      navigateDoctorStep(doctor);
    }, 100);
  };

  const sheetContentMap = getSheetMethodContentMap({
    setShowBottomSheet,
    showBottomSheet,
    handleSpecialtySelect,
    specialties,
    doctors,
    handleDoctorSelect,
  });

  const handleBackdropClick = (e: any) => {
    if (e.target === e.currentTarget) {
      setShowBottomSheet(false);
    }
  };

  const navigateDoctorStep = (doctor: any) => {
    const doctorId = doctor.doctorId ?? doctor.id;
    if (!doctorId) return;

    searchParams.set("type", "doctor");
    searchParams.set("doctorId", String(doctorId));
    searchParams.set("step", "chooseTime");

    setSearchParams(searchParams);
  };

  const navigateSpecialtyStep = (sp: ClinicScheduleStatusDto) => {
    searchParams.set("type", "clinic");
    searchParams.set("specialty", String(sp.clinicId));
    searchParams.set("step", "chooseTime");

    setSearchParams(searchParams);
  };

  const resetData = () => {
    setSelectedDoctor(null);
    setSelectedDate(null);
    setSelectedMonth(null);
    setSelectedYear(null);
    setSelectedTime(null);
  };

  return isMobile ? (
    <div className="h-full bg-white ">
      <div className="p-3 space-y-6 bg-white">
        <p className="text-green-700 font-medium text-lg mt-2 ">
          {t("chonphuongthuc")}
        </p>
        {/* Option 2: Đặt khám theo bác sĩ */}
        <button
          type="button"
          className="w-full text-left border-2 border-slate-200 rounded-2xl p-4 hover:border-emerald-500 transition-colors duration-200 group"
          onClick={() => onOptionSelected("byDoctor")}
        >
          <div className="flex items-center space-x-4">
            <Icon
              icon="healthicons:doctor-male-outline"
              className="w-10 h-10 lg:w-12 lg:h-12 text-green-600"
            />

            <div className="flex-1">
              <p className="text-base font-semibold text-slate-800 text-left">
                {t("khamsangloctongquat")}
              </p>
              <p className="text-sm text-gray-400">
                {t("khamsanglocmota")}
              </p>
            </div>
          </div>
        </button>
        
        {/* Option 1: Đặt khám theo thời gian */}
        <button
          type="button"
          className="w-full text-left border-2 border-slate-200 rounded-2xl p-4 hover:border-emerald-500 transition-colors duration-200 group"
          onClick={() => onOptionSelected("byTime")}
        >
          <div className="flex items-center space-x-4">
            <Icon
              icon="healthicons:stethoscope-outline"
              className="w-10 h-10 text-green-600"
            />
            <div className="flex-1">
              <p className="text-base font-semibold text-slate-800 text-left">
                {t("khamchuyenkhoa")}
              </p>
              <p className="text-sm text-gray-400">
                {t("khamchuyenkhoamota")}
              </p>
            </div>
          </div>
        </button>

        
      </div>

      {/* Bottom Sheet */}
      <div
        className={clsx(
          "fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-end duration-300",
          showBottomSheet
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none",
        )}
        onClick={(e) => handleBackdropClick(e)}
      >
        <div
          className={clsx(
            "bg-white h-[80%] w-full rounded-t-2xl transform transition-transform duration-300",
            showBottomSheet ? "translate-y-0" : "translate-y-full",
          )}
        >
          {bottomSheetType !== undefined && showBottomSheet && (
            <div>
              {/* Header */}
              <div className="flex items-center justify-between p-3 border-b border-gray-200">
                <button
                  onClick={() => setShowBottomSheet(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
                <p className="text-base font-medium text-gray-900">
                  {sheetTitleMap[bottomSheetType]}
                </p>
                <div className="w-9 h-9"></div>
              </div>

              {/* Body */}
              {sheetContentMap[bottomSheetType]}
            </div>
          )}
        </div>
      </div>
    </div>
  ) : (
    <SelectMethodDialog isOpen={true} ableClose={false} />
  );
};
